// @ts-nocheck
// app/lead-gate.tsx
import { View, StyleSheet, Platform, ActivityIndicator, Text, Pressable } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useState, useEffect, useCallback } from 'react';
import Head from 'expo-router/head';
import { LeadCaptureGate } from '@/components/LeadCaptureGate';
import { theme } from '@/constants/theme';
import { LeadGateData } from '@/types/chat';
import { conversationService } from '@/utils/conversationService';

type LeadGateCourse = {
  id?: string | number;
  course_id?: string | number;
  title?: string;
  name?: string;
  provider?: string;
  mentor?: string;
  topic?: string;
  subTopic?: string;
  sub_topic?: string;
  format?: string;
  learningType?: string;
  level?: string;
  language?: string;
  summary?: string;
  description?: string;
  price?: string;
  duration?: string;
  audience?: string;
  outcomes?: string;
  source_url?: string;
};

function getSingleParam(params: any, key: string): string {
  const raw = params?.[key];
  if (raw == null) return '';
  return Array.isArray(raw) ? String(raw[0] ?? '') : String(raw ?? '');
}

function parseCoursesParam(raw: string): any[] {
  if (!raw) return [];

  try {
    const decoded = decodeURIComponent(raw);
    const parsed = JSON.parse(decoded);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}

function normalizeCourses(rawCourses: LeadGateCourse[]): any[] {
  return (rawCourses || []).map((course, index) => ({
    id: course.id ?? course.course_id ?? `idx_${index}`,
    course_id: course.course_id ?? course.id ?? `idx_${index}`,
    name: course.name || course.title || `אפשרות ${index + 1}`,
    title: course.title || course.name || `אפשרות ${index + 1}`,
    mentor: course.mentor || course.provider || '',
    provider: course.provider || course.mentor || '',
    topic: course.topic || '',
    subTopic: course.subTopic || course.sub_topic || '',
    sub_topic: course.sub_topic || course.subTopic || '',
    learningType: course.learningType || course.format || '',
    format: course.format || course.learningType || '',
    level: course.level || '',
    language: course.language || '',
    summary: course.summary || course.description || '',
    description: course.description || course.summary || '',
    price: course.price || '',
    duration: course.duration || '',
    audience: course.audience || '',
    outcomes: course.outcomes || '',
    source_url: course.source_url || '',
  }));
}

export default function LeadGateScreen() {
  const router = useRouter();
  const params = useLocalSearchParams();

  const [leadData, setLeadData] = useState<LeadGateData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fatalError, setFatalError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = () => {
      try {
        setIsLoading(true);
        setFatalError(null);

        // ✅ FLOW חדש: נתונים מגיעים ישירות ב-params
        const directCoursesRaw = getSingleParam(params, 'courses');
        const directMatchCountRaw = getSingleParam(params, 'matchCount');
        const directTopic = getSingleParam(params, 'topic');
        const directSubTopic = getSingleParam(params, 'subTopic');
        const directConversationId = getSingleParam(params, 'conversationId');
        const directSource = getSingleParam(params, 'source');

        const directCourses = normalizeCourses(parseCoursesParam(directCoursesRaw));
        const directMatchCount =
          Number(directMatchCountRaw) > 0 ? Number(directMatchCountRaw) : directCourses.length;

        if (directCourses.length > 0) {
          const directLeadData: LeadGateData = {
            matchCount: directMatchCount,
            userSummary: directTopic
              ? `התאמה עבור תחום ${directTopic}${directSubTopic ? ` / ${directSubTopic}` : ''}`
              : 'התאמת קורסים מתוך הצ׳אט',
            searchData: {
              topic: directTopic || null,
              subtopic: directSubTopic || null,
              conversationId: directConversationId || null,
              source: directSource || 'chat_results',
            } as any,
            courses: directCourses as any,
          };

          if (!cancelled) {
            setLeadData(directLeadData);
            setIsLoading(false);
          }
          return;
        }

        // ✅ FLOW ישן: id + localStorage
        const leadGateId = getSingleParam(params, 'id');

        if (!leadGateId) {
          setFatalError('לא מצאנו את פרטי ההתאמה. חזור לצ׳אט ונסה שוב.');
          setIsLoading(false);
          return;
        }

        if (Platform.OS !== 'web' || typeof window === 'undefined') {
          setFatalError('דף הלידים זמין דרך הדפדפן בלבד בשלב הזה.');
          setIsLoading(false);
          return;
        }

        let storedData: string | null = null;

        try {
          storedData = window.localStorage?.getItem(leadGateId) ?? null;
        } catch {
          setFatalError('הדפדפן חסם גישה לאחסון מקומי. נסה חלון רגיל או דפדפן אחר.');
          setIsLoading(false);
          return;
        }

        if (!storedData) {
          setFatalError('לא מצאנו את הפרטים. חזור לצ׳אט ונסה שוב.');
          setIsLoading(false);
          return;
        }

        const parsed = JSON.parse(storedData) as LeadGateData;

        if (!parsed || !Array.isArray((parsed as any).courses) || typeof (parsed as any).matchCount !== 'number') {
          setFatalError('המידע שהתקבל לא תקין. חזור לצ׳אט ונסה שוב.');
          setIsLoading(false);
          return;
        }

        if (!cancelled) {
          setLeadData(parsed);
        }

        try {
          window.localStorage?.removeItem(leadGateId);
        } catch {
          // ignore cleanup failure
        }

        if (!cancelled) {
          setIsLoading(false);
        }
      } catch {
        if (!cancelled) {
          setFatalError('לא הצלחנו לטעון את הפרטים. חזור לצ׳אט ונסה שוב.');
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [params]);

  const exitToChat = useCallback(() => {
    router.replace('/chat');
  }, [router]);

  const handleSubmit = useCallback(
    async (data: {
      fullName: string;
      phone: string;
      selectedMentorCount: number;
      consentGiven: boolean;
      preferredTime?: 'morning' | 'afternoon' | 'evening';
    }) => {
      if (!leadData) return;

      try {
        const payload = {
          source: 'lead-gate',
          submittedAt: new Date().toISOString(),
          ...data,
          leadGate: {
            matchCount: leadData.matchCount,
            userSummary: (leadData as any).userSummary,
            searchData: (leadData as any).searchData,
            courses: leadData.courses,
          },
        };

        const svc: any = conversationService as any;
        let submitResult: { success?: boolean; error?: string } | null = null;
        if (typeof svc?.submitLead === 'function') {
          submitResult = await svc.submitLead(payload);
        } else if (typeof svc?.submitLeadGate === 'function') {
          submitResult = await svc.submitLeadGate(payload);
        } else if (typeof svc?.createLead === 'function') {
          submitResult = await svc.createLead(payload);
        }

        if (submitResult && submitResult.success === false) {
          throw new Error(submitResult.error || 'Lead submit failed');
        }

        router.replace({
          pathname: '/thank-you',
          params: {
            source: 'lead-gate',
            name: data.fullName,
            mentorCount: String(data.selectedMentorCount),
          },
        });
      } catch {
        setFatalError('השליחה נכשלה. בדוק חיבור אינטרנט ונסה שוב.');
      }
    },
    [leadData, router]
  );

  const handleEmailSubmit = useCallback(
    async (email: string) => {
      if (!leadData) return;

      try {
        const payload = {
          source: 'lead-gate-email',
          submittedAt: new Date().toISOString(),
          email,
          leadGate: {
            matchCount: leadData.matchCount,
            userSummary: (leadData as any).userSummary,
            searchData: (leadData as any).searchData,
            courses: leadData.courses,
          },
        };

        const svc: any = conversationService as any;
        if (typeof svc?.submitLeadEmail === 'function') {
          await svc.submitLeadEmail(payload);
        } else if (typeof svc?.submitLead === 'function') {
          await svc.submitLead(payload);
        } else if (typeof svc?.createLead === 'function') {
          await svc.createLead(payload);
        }
      } catch {
        setFatalError('שמירת האימייל נכשלה. נסה שוב.');
      }
    },
    [leadData]
  );

  const handleSkip = useCallback(() => {
    exitToChat();
  }, [exitToChat]);

  const handleComplete = useCallback(() => {
    router.replace('/thank-you');
  }, [router]);

  if (isLoading) {
    return (
      <View style={styles.container}>
        <ActivityIndicator />
      </View>
    );
  }

  if (fatalError || !leadData) {
    return (
      <View style={styles.container}>
        <View style={styles.errorCard}>
          <Text style={styles.errorTitle}>משהו השתבש</Text>
          <Text style={styles.errorText}>{fatalError || 'לא הצלחנו לטעון את הפרטים.'}</Text>

          <Pressable onPress={exitToChat} style={styles.errorButton}>
            <Text style={styles.errorButtonText}>חזרה לצ׳אט</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {Platform.OS === 'web' && (
        <Head>
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href="https://kursiaii.com/lead-gate" />
        </Head>
      )}

      <LeadCaptureGate
        matchCount={leadData.matchCount}
        userSummary={(leadData as any).userSummary}
        searchData={(leadData as any).searchData}
        courses={leadData.courses}
        onSubmit={handleSubmit}
        onSkip={handleSkip}
        onEmailSubmit={handleEmailSubmit}
        onComplete={handleComplete}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F2EBDD',
    justifyContent: 'center',
    alignItems: 'center',
    padding: Platform.OS === 'web' ? 0 : 16,
  },
  errorCard: {
    width: '100%',
    maxWidth: 520,
    backgroundColor: '#FAF5EA',
    padding: 32,
    borderWidth: 1,
    borderColor: '#1A1611',
  },
  errorTitle: {
    fontSize: 28,
    fontFamily: 'FrankRuhlLibre-Bold',
    color: '#1A1611',
    marginBottom: 10,
    textAlign: 'right',
    letterSpacing: -0.5,
  },
  errorText: {
    fontSize: 15,
    fontFamily: 'Heebo-Regular',
    color: '#3A332A',
    lineHeight: 24,
    textAlign: 'right',
    marginBottom: 20,
  },
  errorButton: {
    backgroundColor: '#1A1611',
    paddingVertical: 14,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#1A1611',
  },
  errorButtonText: {
    fontSize: 14,
    fontFamily: 'Heebo-Bold',
    color: '#F2EBDD',
    letterSpacing: 0.3,
  },
});
